import { CircularProgress, Divider } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useCreate, useCustom } from "@refinedev/core";
import { useEffect, useState } from "react";
import FundUserSelectionTable from "components/inputs/custom-user-multiple-selection";
import { FundAccountMember } from "interfaces/fund-account-member";
import { IUser } from "interfaces/user";
import { GrantVestmentAuthorizedViewers } from "interfaces/grant-vestment";

interface ModalProps {
  visible: boolean;
  close: React.Dispatch<React.SetStateAction<boolean>>;
  grantvestmentId: number;
  refetch: any;
  authorizedViewers: GrantVestmentAuthorizedViewers[];
}

export const GrantvestmentUpdatesVisibilityModal: React.FC<ModalProps> = ({
  visible,
  close,
  grantvestmentId,
  refetch,
  authorizedViewers,
}) => {
  const { mutate, isLoading, isSuccess: addedSuccessfully } = useCreate();

  const [loading, setLoading] = useState(true);
  const [selectedUserSubs, setSelectedUserSubs] = useState<string[]>([]);

  const {
    data: response,
    isSuccess,
    isFetching,
    refetch: refetchUnauthorizedUsers,
  } = useCustom({
    url: `grant-vestments/${grantvestmentId}/unauthorized`,
    method: "get",
  });

  useEffect(() => {
    if (addedSuccessfully) {
      refetchUnauthorizedUsers();
    }
  }, [addedSuccessfully, refetchUnauthorizedUsers]);

  const [funds, setFunds] = useState<
    { id: string; name: string; users: IUser[] }[]
  >([]);
  // Process API response to format needed by FundUserSelectionTable
  useEffect(() => {
    if (isSuccess && response?.data) {
      const formattedFunds = response.data.map(
        (fund: {
          fundAccountId: number;
          fundAccountName: string;
          fundAccountMembers: FundAccountMember[];
        }) => ({
          id: fund.fundAccountId,
          name: fund.fundAccountName,
          users: fund.fundAccountMembers
            .filter(
              (member) =>
                !authorizedViewers.some(
                  (viewer) => viewer.user.sub === member.user.sub
                )
            )
            .map((member) => ({
              id: member.user.userId,
              name: `${member.user.firstName} ${member.user.lastName}`,
              sub: member.user.sub,
              role: member.role,
            })),
        })
      );
      setFunds(formattedFunds);
      setLoading(false);
    }
  }, [response, isSuccess, authorizedViewers]);

  // Handle user selection change and update selectedUserSub
  const handleSelectionChange = (userSubs: string[] | any[]) => {
    setSelectedUserSubs(Array.isArray(userSubs) ? userSubs : []);
  };

  return (
    <Dialog open={visible} onClose={() => close(false)} fullScreen>
      <DialogTitle color="primary">
        Allow user to view the GrantVestment updates
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ minHeight: 300 }}>
        <FundUserSelectionTable
          funds={funds as any}
          loading={loading || isFetching}
          onSelectionChange={handleSelectionChange}
        />
      </DialogContent>
      <Divider />
      <DialogActions>
        <>
          <Button
            variant="contained"
            disabled={isLoading || selectedUserSubs.length === 0}
            startIcon={
              isLoading && <CircularProgress size={20} color="inherit" />
            }
            onClick={() => {
              mutate(
                {
                  resource: `grant-vestments/${grantvestmentId}/authorize`,
                  values: {
                    userSub: selectedUserSubs,
                  },
                },
                {
                  onSuccess: () => {
                    refetch();
                    close(false);
                  },
                }
              );
            }}
          >
            Confirm
          </Button>
          <Button variant="outlined" onClick={() => close(false)}>
            Cancel
          </Button>
        </>
      </DialogActions>
    </Dialog>
  );
};
